import { AlertTriangle, Landmark } from "lucide-react";
import type { ChangeEvent } from "react";
import { FieldWrapper } from "@/components/ui/FieldWrapper";
import Input from "@/components/ui/Input";
import Select from "@/components/ui/Select";
import Checkbox from "@/components/ui/Checkbox";
import UploadBlock from "./UploadBlock";
import { BANK_OPTIONS, resolveBank } from "../data";
import { KYC_FEE } from "@/pages/retailer/wallet/constants";
import { formatINR } from "../data";
import type { FormErrors, KycForm, SetDoc, SetField } from "../types";

interface Props {
  form: KycForm;
  errors: FormErrors;
  setField: SetField;
  setDoc: SetDoc;
  setError: (key: string, message: string) => void;
  confirmChecked: boolean;
  onConfirmChange: (v: boolean) => void;
  retailerId?: string;
  kycFee?: number;
}

export default function BankDetailsStep({
  form,
  errors,
  setField,
  setDoc,
  setError,
  confirmChecked,
  onConfirmChange,
  retailerId,
  kycFee,
}: Props) {
  const resolved = resolveBank(form.bankName, form.ifsc);

  function handleAccountChange(e: ChangeEvent<HTMLInputElement>) {
    setField("accountNumber", e.target.value.replace(/\D/g, ""));
  }

  function handleConfirmBlur() {
    const val = form.confirmAccountNumber.trim();
    if (val && val !== form.accountNumber) {
      setError("confirmAccountNumber", "Account numbers do not match");
    } else {
      setError("confirmAccountNumber", "");
    }
  }

  return (
    <div>
      <h3 className="text-lg font-semibold text-ink">Bank Details</h3>
      <p className="text-[13px] text-ink-secondary">
        Settlement of your collections will be credited to this account
      </p>

      <div className="mt-6 grid grid-cols-1 gap-x-5 gap-y-5 md:grid-cols-2">
        <Input
          label="Account Holder Name"
          required
          error={errors.accountHolderName}
          helper="Must match the name on your business or personal PAN"
          placeholder="Name on the account"
          id="accountHolderName"
          value={form.accountHolderName}
          onChange={(e) => setField("accountHolderName", e.target.value)}
        />
        <FieldWrapper
          label="Account Number"
          required
          error={errors.accountNumber}
          htmlFor="accountNumber"
        >
          <input
            id="accountNumber"
            inputMode="numeric"
            maxLength={18}
            placeholder="Enter account number"
            value={form.accountNumber}
            onChange={handleAccountChange}
            className={`h-[42px] w-full rounded-lg border bg-white px-3.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 ${
              errors.accountNumber
                ? "border-danger focus:ring-danger/30"
                : "border-slate-300 focus:border-primary-500 focus:ring-primary-500/30"
            }`}
          />
        </FieldWrapper>
        <Input
          label="Confirm Account Number"
          required
          error={errors.confirmAccountNumber}
          id="confirmAccountNumber"
          inputMode="numeric"
          maxLength={18}
          placeholder="Re-enter account number"
          value={form.confirmAccountNumber}
          onChange={(e) => setField("confirmAccountNumber", e.target.value.replace(/\D/g, ""))}
          onBlur={handleConfirmBlur}
        />
        <Input
          label="IFSC Code"
          required
          error={errors.ifsc}
          helper="Format: ABCD0123456"
          placeholder="Format: ABCD0123456"
          id="ifsc"
          maxLength={11}
          value={form.ifsc}
          onChange={(e) => setField("ifsc", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
        />
        <Select
          label="Bank Name"
          required
          placeholder="Select bank"
          options={BANK_OPTIONS}
          error={errors.bankName}
          id="bankName"
          value={form.bankName}
          onChange={(e) => setField("bankName", e.target.value)}
        />
        <div className="md:col-span-2">
          <FieldWrapper label="Account Type" required error={errors.accountType}>
            <div id="accountType" className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {(["Savings", "Current"] as const).map((t) => {
                const selected = form.accountType === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setField("accountType", t)}
                    className={`flex items-center justify-between rounded-lg border px-4 py-3.5 text-left transition-colors cursor-pointer ${
                      selected
                        ? "border-primary-500 bg-primary-50"
                        : "border-slate-300 bg-white hover:border-slate-400"
                    }`}
                  >
                    <div>
                      <p className="text-sm font-medium text-ink">{t} Account</p>
                      <p className="text-xs text-ink-secondary">
                        {t === "Savings" ? "For personal savings" : "For business transactions"}
                      </p>
                    </div>
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                        selected ? "border-primary-500" : "border-slate-300"
                      }`}
                    >
                      {selected && <span className="h-2 w-2 rounded-full bg-primary-500" />}
                    </span>
                  </button>
                );
              })}
            </div>
          </FieldWrapper>
        </div>
      </div>

      {resolved && (
        <div className="mt-5 flex items-center gap-2.5 rounded-lg bg-info-bg px-3.5 py-2.5">
          <Landmark className="h-4 w-4 shrink-0 text-info" />
          <p className="text-[13px] text-ink-secondary">{resolved}</p>
        </div>
      )}

      <div className="mt-6">
        <UploadBlock
          label="Cancelled Cheque or Bank Statement"
          helper="A cancelled cheque or a recent bank statement with your account name and number"
          value={form.docs.cancelledCheque ?? null}
          onChange={(d) => setDoc("cancelledCheque", d)}
          retailerId={retailerId}
        />
        <div className="mt-3 flex items-start gap-2.5 rounded-lg border border-warning/40 bg-warning-bg px-3.5 py-2.5">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <p className="text-[13px] text-ink-secondary">
            Please do not skip this. Uploading a cancelled cheque lets us cross-verify your account
            details and avoids settlement failures later.
          </p>
        </div>
      </div>

      <div className="mt-6 border-t border-line pt-5">
        <Checkbox
          checked={confirmChecked}
          onChange={(e) => onConfirmChange(e.target.checked)}
          error={errors.confirmation}
          label={`I confirm that all the information and documents provided are true and belong to my business. I understand that the verification fee of ${formatINR(kycFee || KYC_FEE)} will be deducted from my wallet on submission.`}
        />
      </div>
    </div>
  );
}